import { materializeStockCommand } from '@trackeat/shared';
import { openLocalDb } from '../db/local.js';
import {
  listStockCommandReconciliations,
  resolveStockCommandReconciliation,
  type StockCommandReconciliation,
} from './stockCommandSqlite.js';

export interface ConfirmedStockClosureEvent {
  id: string;
  server_id: string;
  tenant_id: string;
  business_id: string;
  kind: string;
  client_ts: string;
  rev: number;
  self_hash: string;
  payload: Record<string, unknown>;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LOCATOR = ['food_id', 'batch_lot', 'expiration_date', 'supplier_id', 'document_id', 'storage_unit_id', 'stock_uom'] as const;

/** A later accepted close supersedes a rejected retry; it is not a physical count. */
export function supersedingStockClosure(
  rejected: StockCommandReconciliation,
  events: readonly ConfirmedStockClosureEvent[],
): string | null {
  const command = rejected.command;
  if (rejected.reason === 'stock_command_v1_event_1_document_not_in_tenant'
    && command.movement_class === 'lot_state' && command.lines.length === 1
    && command.lines[0].action === 'close' && rejected.master_effects.length === 0) {
    const expected = materializeStockCommand(command)[0]?.payload;
    const proof = events.find((event) => event.tenant_id === command.tenant_id
      && event.business_id === command.business_id && event.kind === 'stock_out'
      && event.rev > 0 && UUID.test(event.server_id) && /^[0-9a-f]{64}$/.test(event.self_hash)
      && event.payload.source_line_ref === `support-recovery:${command.command_id}`
      && event.payload.stock_qty === 0 && event.payload.lot_closed === true
      && event.payload.document_id === null
      && LOCATOR.filter((key) => key !== 'document_id')
        .every((key) => (event.payload[key] ?? null) === (expected?.[key] ?? null)));
    return proof?.server_id ?? null;
  }
  if (rejected.reason !== 'stock_command_v1_event_1_metadata_mismatch:operator_name'
    || command.movement_class !== 'consume' || command.lines.length !== 1
    || command.lines[0].close_lot !== true || rejected.master_effects.length !== 0
    || rejected.events.length !== 1) return null;
  const expected = materializeStockCommand(command)[0]?.payload;
  if (!expected) return null;
  const matches = events.filter((event) => event.tenant_id === command.tenant_id
    && event.business_id === command.business_id && event.rev > 0
    && UUID.test(event.server_id) && /^[0-9a-f]{64}$/.test(event.self_hash)
    && LOCATOR.every((key) => (event.payload[key] ?? null) === (expected[key] ?? null)))
    .sort((a, b) => Date.parse(b.client_ts) - Date.parse(a.client_ts) || b.rev - a.rev);
  const latest = matches[0];
  if (!latest || latest.kind !== 'stock_out' || latest.payload.lot_closed !== true
    || latest.payload.stock_qty !== expected.stock_qty
    || Date.parse(latest.client_ts) < Date.parse(rejected.events[0].client_ts)) return null;
  return latest.server_id;
}

export async function resolveSupersededStockClosures(tenantId: string): Promise<number> {
  const rows = (await listStockCommandReconciliations({ tenantId })).filter((row) => (
    (row.reason === 'stock_command_v1_event_1_metadata_mismatch:operator_name'
      && row.command.movement_class === 'consume')
    || (row.reason === 'stock_command_v1_event_1_document_not_in_tenant'
      && row.command.movement_class === 'lot_state')
  ));
  if (rows.length === 0) return 0;
  const foods = [...new Set(rows.flatMap((row) => (row.command.movement_class === 'consume' || row.command.movement_class === 'lot_state')
    ? row.command.lines.map((line) => line.food_id) : []))];
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT id, server_id, tenant_id, business_id, kind, client_ts, rev, self_hash, payload
       FROM events_local WHERE tenant_id = ? AND rev > 0
        AND kind IN ('stock_in','stock_out','stock_lot_state')
        AND json_extract(payload, '$.food_id') IN (${foods.map(() => '?').join(',')})`,
    [tenantId, ...foods],
  );
  const events = (result.values ?? []).map((row) => ({
    ...row, payload: JSON.parse(row.payload as string) as Record<string, unknown>,
  })) as ConfirmedStockClosureEvent[];
  let count = 0;
  for (const row of rows) {
    const proof = supersedingStockClosure(row, events);
    if (!proof) continue;
    await resolveStockCommandReconciliation({
      commandId: row.command_id, tenantId,
      resolution: `superseded_by_confirmed_lot_close:${proof}`,
    });
    count += 1;
  }
  return count;
}
